import React, {
  type CSSProperties,
  type FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { type ImperativePanelHandle, getPanelGroupElement } from 'react-resizable-panels';

import BaseResizeHandle from './base-resize-handle';

const PANEL_GROUP_ID = 'panel-group';

interface PanelResizeHandleProps {
  id: string;
  disabled?: boolean;
  style?: CSSProperties;
  onDragging?: (isDragging: boolean) => void;
  panelRef: React.RefObject<ImperativePanelHandle | null>;
  minSize: number;
  maxSize: number;
}

const PanelResizeHandle: FunctionComponent<PanelResizeHandleProps> = ({
  id,
  disabled = false,
  style,
  onDragging,
  panelRef,
  minSize,
  maxSize,
}) => {
  const [currentSize, setCurrentSize] = useState<number | null>(null);

  useEffect(() => {
    if (panelRef.current) {
      setCurrentSize(panelRef.current.getSize());
    }
  }, [panelRef]);

  const handlePointerResize = useCallback(
    (delta: number, startSize: number) => {
      if (!panelRef.current) {
        return;
      }

      const groupElement = getPanelGroupElement(PANEL_GROUP_ID);
      if (!groupElement) {
        return;
      }

      const groupRect = groupElement.getBoundingClientRect();
      const deltaPercent = (delta / groupRect.width) * 100;
      const newSize = Math.max(minSize, Math.min(maxSize, startSize + deltaPercent));

      panelRef.current.resize(newSize);
      setCurrentSize(newSize);
    },
    [panelRef, minSize, maxSize]
  );

  const handleKeyboardResize = useCallback(
    (delta: number, startSize: number) => {
      if (!panelRef.current) {
        return;
      }

      const newSize = Math.max(minSize, Math.min(maxSize, startSize + delta));
      panelRef.current.resize(newSize);
      setCurrentSize(newSize);
    },
    [panelRef, minSize, maxSize]
  );

  return (
    <BaseResizeHandle
      id={id}
      disabled={disabled}
      style={style}
      onDragging={onDragging}
      minSize={minSize}
      maxSize={maxSize}
      currentSize={currentSize}
      onPointerResize={handlePointerResize}
      onKeyboardResize={handleKeyboardResize}
    />
  );
};

export default PanelResizeHandle;
